/**
 * RecordingAdminsCard — per-customer list of phone numbers authorized to
 * dial in and record audio. Each row has a display name, masked E.164,
 * status badge (Verified / Unverified / Inactive), and per-row actions:
 *
 *   - Verify / Reauthenticate   → opens VerifyAdminDialog (OTP-callback)
 *   - Set / change PIN           → opens SetPinDialog
 *   - Disable / enable           → manual is_active toggle
 *   - Delete                     → soft-delete the row
 *
 * Rows that have not been verified yet show a prominent "Verify" CTA.
 * Rows whose `last_authenticated_at` is older than ~90 days come back
 * from the API with `is_active=false` and surface an "Inactive" badge
 * + Reauthenticate button.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { customerRecordingAdmins, type RecordingAdminRow } from '@/api/customerRecordingAdmins';
import { PhoneInput } from '@/components/PhoneInput';
import { Plus, Phone, KeyRound, Trash2, ShieldCheck, RefreshCcw } from 'lucide-react';

const E164_RE = /^\+[1-9]\d{1,14}$/;

function maskE164(e164: string): string {
  // Mask middle digits: +1555***4567 — give enough context to recognise but
  // not enough to dial. Last 4 + country prefix shown.
  if (e164.length <= 8) return e164;
  return `${e164.slice(0, 4)}***${e164.slice(-4)}`;
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function StatusBadge({ row }: { row: RecordingAdminRow }) {
  if (!row.is_active) return <Badge variant="destructive">Inactive (90+ d)</Badge>;
  if (!row.verified_at) return <Badge variant="secondary">Unverified</Badge>;
  if (!row.has_pin) return <Badge variant="secondary">No PIN set</Badge>;
  return <Badge variant="default">Active</Badge>;
}

export function RecordingAdminsCard({ customerId }: { customerId: string }) {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [verifyTarget, setVerifyTarget] = useState<RecordingAdminRow | null>(null);
  const [pinTarget, setPinTarget] = useState<RecordingAdminRow | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['recordingAdmins', customerId],
    queryFn: () => customerRecordingAdmins.list(customerId),
    enabled: Boolean(customerId),
  });
  const rows = data?.rows ?? [];

  const remove = useMutation({
    mutationFn: (id: string) => customerRecordingAdmins.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recordingAdmins', customerId] }),
  });

  const toggleActive = useMutation({
    mutationFn: (row: RecordingAdminRow) =>
      customerRecordingAdmins.update(row.id, { is_active: !row.is_active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recordingAdmins', customerId] }),
  });

  return (
    <>
      <Card>
        <CardHeader className="flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle className="text-base">Recording admin numbers</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Phone numbers authorized to dial in and record audio for this
              customer. Each registered number must be verified by phone
              once and given a PIN before it can be used.
            </p>
          </div>
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" /> Add number
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading && (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error instanceof Error ? error.message : 'Failed to load admin numbers'}
            </p>
          )}
          {!isLoading && rows.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No recording admins yet. Click <em>Add number</em> to register one.
            </p>
          )}
          {rows.length > 0 && (
            <div className="divide-y divide-border">
              {rows.map((row) => (
                <div key={row.id} className="flex flex-wrap items-center gap-3 py-3">
                  <div className="flex-1 min-w-[12rem]">
                    <div className="font-medium">{row.display_name}</div>
                    <div className="text-xs text-muted-foreground font-mono tabular-nums">
                      {maskE164(row.e164)}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Last auth: {formatRelative(row.last_authenticated_at)}
                  </div>
                  <StatusBadge row={row} />
                  <div className="flex items-center gap-1">
                    {(!row.verified_at || !row.is_active) ? (
                      <Button
                        size="sm"
                        variant={!row.is_active ? 'default' : 'outline'}
                        onClick={() => setVerifyTarget(row)}
                      >
                        {row.is_active ? (
                          <><ShieldCheck className="h-4 w-4" /> Verify</>
                        ) : (
                          <><RefreshCcw className="h-4 w-4" /> Reauthenticate</>
                        )}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setVerifyTarget(row)}
                      >
                        Re-verify
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setPinTarget(row)}
                    >
                      <KeyRound className="h-4 w-4" /> {row.has_pin ? 'Change PIN' : 'Set PIN'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={toggleActive.isPending}
                      onClick={() => toggleActive.mutate(row)}
                    >
                      {row.is_active ? 'Disable' : 'Enable'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (confirm(`Delete ${row.display_name}? This cannot be undone via the UI.`)) {
                          remove.mutate(row.id);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AddAdminDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        customerId={customerId}
        onSaved={() => qc.invalidateQueries({ queryKey: ['recordingAdmins', customerId] })}
      />
      <VerifyAdminDialog
        admin={verifyTarget}
        onOpenChange={(open) => { if (!open) setVerifyTarget(null); }}
        onVerified={() => qc.invalidateQueries({ queryKey: ['recordingAdmins', customerId] })}
      />
      <SetPinDialog
        admin={pinTarget}
        onOpenChange={(open) => { if (!open) setPinTarget(null); }}
        onSaved={() => qc.invalidateQueries({ queryKey: ['recordingAdmins', customerId] })}
      />
    </>
  );
}

// ── Add admin dialog ─────────────────────────────────────────────────────────

function AddAdminDialog({
  open, onOpenChange, customerId, onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  customerId: string;
  onSaved: () => void;
}) {
  const [displayName, setDisplayName] = useState('');
  const [e164, setE164] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      customerRecordingAdmins.create({ customer_id: customerId, display_name: displayName, e164 }),
    onSuccess: () => {
      onSaved();
      setDisplayName(''); setE164(''); setError(null);
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('e164_already_registered')) {
        setError('This number is already registered for this customer.');
      } else {
        setError(msg || 'Failed to add number');
      }
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    setError(null);
    if (!displayName.trim()) {
      setError('Display name is required.');
      return;
    }
    if (!E164_RE.test(e164.trim())) {
      setError('Enter a valid phone number.');
      return;
    }
    create.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add recording admin number</DialogTitle>
          <DialogDescription>
            Register a phone number that&rsquo;s allowed to record audio for
            this customer. After saving, click <em>Verify</em> to send the
            number a verification call.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="admin-name">Display name</Label>
            <Input
              id="admin-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Office mobile"
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="admin-e164">Phone number</Label>
            <PhoneInput
              id="admin-e164"
              value={e164}
              onChange={setE164}
              customerId={customerId}
              placeholder="212 555 0100"
            />
            <p className="text-xs text-muted-foreground">
              The verification call will dial this number. Change the country
              prefix if the number is outside the default region.
            </p>
          </div>
          {error && (
            <p role="alert" className="text-sm text-destructive">{error}</p>
          )}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Adding…' : 'Add number'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Verify admin dialog (OTP-callback) ───────────────────────────────────────

function VerifyAdminDialog({
  admin, onOpenChange, onVerified,
}: {
  admin: RecordingAdminRow | null;
  onOpenChange: (open: boolean) => void;
  onVerified: () => void;
}) {
  const [phase, setPhase] = useState<'idle' | 'calling' | 'enter' | 'success' | 'error'>('idle');
  const [pin, setPin] = useState('');
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  const start = useMutation({
    mutationFn: () => customerRecordingAdmins.startVerification(admin!.id),
    onSuccess: () => {
      setPhase('enter');
      setStatusMsg('We just called the number. Listen for the 4-digit code.');
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : '';
      setPhase('error');
      if (msg.includes('reseller_recording_dial_in_e164_not_set')) {
        setStatusMsg('The reseller has not set a recording dial-in number — we need that for caller ID before we can place the verification call. Set it on the reseller page first.');
      } else {
        setStatusMsg('Failed to place the verification call. Please try again.');
      }
    },
  });

  const submit = useMutation({
    mutationFn: () => customerRecordingAdmins.verify(admin!.id, pin),
    onSuccess: (res) => {
      if (res.ok) {
        setPhase('success');
        setStatusMsg('Number verified. The admin can now dial in.');
        onVerified();
      } else {
        const k = (res as { kind: string; attemptsLeft?: number }).kind;
        if (k === 'wrong') {
          setStatusMsg(`That code is incorrect. ${('attemptsLeft' in res) ? `${(res as { attemptsLeft: number }).attemptsLeft} attempts left.` : ''}`);
        } else if (k === 'expired') {
          setStatusMsg('That code has expired. Click "Call me again" to get a new one.');
        } else if (k === 'locked') {
          setStatusMsg('Too many wrong attempts. Click "Call me again" to start over.');
        } else {
          setStatusMsg('No verification call is in progress. Click "Call me again" to start.');
        }
      }
    },
    onError: () => setStatusMsg('Failed to submit code. Please try again.'),
  });

  function handleClose() {
    setPhase('idle');
    setPin('');
    setStatusMsg(null);
    onOpenChange(false);
  }

  function handleStart() {
    if (!admin) return;
    setPhase('calling');
    setStatusMsg(null);
    start.mutate();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!/^\d{4}$/.test(pin)) {
      setStatusMsg('Enter the 4-digit code from the call.');
      return;
    }
    submit.mutate();
  }

  if (!admin) return null;

  return (
    <Dialog open={admin !== null} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Verify {admin.display_name}</DialogTitle>
          <DialogDescription>
            We&rsquo;ll call <span className="font-mono">{maskE164(admin.e164)}</span> and
            speak a 4-digit code. Type the code into the box below.
          </DialogDescription>
        </DialogHeader>

        {phase === 'idle' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              When you click <em>Call me</em> the platform will dial the
              number and speak the verification code. Make sure the phone
              is nearby and ringer is on.
            </p>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button onClick={handleStart}>
                <Phone className="h-4 w-4" /> Call me
              </Button>
            </DialogFooter>
          </div>
        )}

        {phase === 'calling' && (
          <div className="space-y-3">
            <p className="text-sm">Calling… listen for the code.</p>
            <p className="text-xs text-muted-foreground">{statusMsg}</p>
          </div>
        )}

        {phase === 'enter' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-muted-foreground">{statusMsg}</p>
            <div className="space-y-1">
              <Label htmlFor="otp-input">4-digit code</Label>
              <Input
                id="otp-input"
                inputMode="numeric"
                maxLength={4}
                pattern="\d{4}"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                autoFocus
                className="font-mono text-2xl tracking-[0.5em] text-center w-32"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" type="button" onClick={() => { setPhase('idle'); setPin(''); setStatusMsg(null); }}>
                Call me again
              </Button>
              <Button type="submit" disabled={submit.isPending || pin.length !== 4}>
                {submit.isPending ? 'Submitting…' : 'Submit'}
              </Button>
            </DialogFooter>
          </form>
        )}

        {phase === 'success' && (
          <div className="space-y-3">
            <p className="text-sm">{statusMsg}</p>
            <DialogFooter>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </div>
        )}

        {phase === 'error' && (
          <div className="space-y-3">
            <p role="alert" className="text-sm text-destructive">{statusMsg}</p>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleClose}>Close</Button>
              <Button onClick={handleStart}>Try again</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Set / change PIN dialog ──────────────────────────────────────────────────

function SetPinDialog({
  admin, onOpenChange, onSaved,
}: {
  admin: RecordingAdminRow | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [error, setError] = useState<string | null>(null);

  const update = useMutation({
    mutationFn: () => customerRecordingAdmins.update(admin!.id, { new_pin: pin }),
    onSuccess: () => {
      onSaved();
      setPin(''); setPin2(''); setError(null);
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : 'Failed to set PIN'),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    setError(null);
    if (!/^\d{4,8}$/.test(pin)) {
      setError('PIN must be 4 to 8 digits.');
      return;
    }
    if (pin !== pin2) {
      setError('PINs do not match.');
      return;
    }
    update.mutate();
  }

  if (!admin) return null;

  return (
    <Dialog open={admin !== null} onOpenChange={(o) => { if (!o) onOpenChange(false); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{admin.has_pin ? 'Change PIN' : 'Set PIN'}</DialogTitle>
          <DialogDescription>
            This is the PIN <strong>{admin.display_name}</strong> will enter
            when calling in to record. 4–8 digits.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="new-pin">New PIN</Label>
            <Input
              id="new-pin"
              inputMode="numeric"
              type="password"
              maxLength={8}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
              autoFocus
              className="font-mono w-40"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="new-pin-confirm">Confirm PIN</Label>
            <Input
              id="new-pin-confirm"
              inputMode="numeric"
              type="password"
              maxLength={8}
              value={pin2}
              onChange={(e) => setPin2(e.target.value.replace(/\D/g, '').slice(0, 8))}
              className="font-mono w-40"
            />
          </div>
          {error && (
            <p role="alert" className="text-sm text-destructive">{error}</p>
          )}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save PIN'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
